/**
 * Menu aplikace na jednom místě.
 *
 * Do 2. 9. 2026 žila navigační struktura uvnitř `layout/Sidebar.jsx`, takže ji
 * znal jen postranní panel na počítači. Spodní lišta na telefonu (fáze 2
 * redesignu) potřebuje přesně tentýž seznam, jinak by některá role na telefonu
 * o sekci přišla. Proto je struktura tady a čte ji sidebar i lišta.
 *
 * `navItems` je PŘESNÁ kopie původního pole ze Sidebaru, včetně komentářů,
 * pořadí a rolí. Nepřidávalo se ani neubíralo nic. Filtrování rolí níž je
 * doslovný přepis původního řetězce `.filter()` volání ze Sidebaru, aby se
 * menu nemohlo pro žádnou roli změnit. Hlídá to `scripts/check-menu-role.mjs`
 * proti snapshotu pořízenému z původního kódu.
 *
 * Kontext (`ctx`), který filtrování potřebuje:
 *   realRole         skutečná role přihlášeného (kvůli náhledu ownera za roli)
 *   trenerUkoncen    trenér v ukončené spolupráci vidí jen Fakturaci
 *   kalendarZapnuty  kalendář rezervací se trenérům zapíná postupně
 */
import {
  Activity,
  AlertTriangle,
  Building2,
  Scale,
  UserRoundCog,
  Award,
  Banknote,
  BarChart3,
  BookOpen,
  Briefcase,
  Calendar,
  CalendarCheck,
  Tag,
  CalendarClock,
  CalendarPlus,
  CalendarRange,
  ChefHat,
  ClipboardList,
  Clock,
  Coins,
  Cpu,
  Database,
  FileText,
  Flame,
  Globe,
  LineChart,
  Megaphone,
  Target,
  GraduationCap, Bandage,
  HeartHandshake,
  History,
  LayoutDashboard,
  LayoutGrid,
  Layers,
  ListChecks,
  Mail,
  Map as MapIcon,
  MapPin,
  MessageSquare,
  Plane,
  Receipt,
  Server,
  Settings as SettingsIcon,
  ShieldCheck,
  Smartphone,
  Soup,
  TrendingUp,
  Trophy,
  Umbrella,
  UserCircle,
  UserCog,
  UserPlus,
  Users,
  Wallet,
  Zap,
  MessageCircleQuestion,
} from "lucide-react";

// Navigační struktura. Každá položka má `roles` array — kdo ji vidí.
// Sidebar i spodní lišta filtrují podle role aktuálně přihlášeného uživatele.
export const navItems = [
  // === Manažer Dashboard — operativní přehled, samostatná top položka (bez dropdownu) ===
  { to: "/manager/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["manager"] },
  // === CSM Dashboard — operativní přehled Client Success Managera, samostatná top položka ===
  { to: "/csm/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["csm"] },
  // === Rezervace (29.9.2026) — vlastní rezervační systém místo Reservia: seznam
  // tréninků a konzultací po měsících (zrušené a propadlé zvlášť) a kalendář týmu.
  // Owner, manager a CSM. Trenéři mají svůj kalendář v trenérském portálu,
  // nutriční stránku Konzultace. Sales ji od 1. 10. 2026 nemá, volné termíny
  // jí ukazuje průvodce hovorem.
  // === Sales (1. 10. 2026): ploché menu se čtyřmi položkami, schválený mockup
  // „Rozhraní Miluše“, varianta A. Čtvrtá položka Úkoly je níž (sdílená s nutritionist).
  // Firemní přehledy (CEO) sales nevidí, server jí je taky nepošle.
  { to: "/sales/dnes", label: "Volání", icon: HeartHandshake, roles: ["sales"], badgeKey: "sales_dnes" },
  // 1. 11. 2026 (zadání Kristiny): obvolávání klientek po nutriční konzultaci, hned pod Voláním.
  // Owner ji má v sekci Sales, CSM v Clients management. Server kontroluje roli (owner, csm, sales).
  { to: "/hodnoceni-vyzivy", label: "Hodnocení výživy", icon: Soup, roles: ["sales"], hideRealRoles: ["manager"], badgeKey: "hodnoceni_vyzivy" },
  // 20. 10. 2026 (Láďa): vzorové rozvrhy trenérů jen ke čtení, samostatná položka menu.
  { to: "/rozvrhy-treneru", label: "Rozvrhy trenérů", icon: CalendarRange, roles: ["sales"] },
  // 1. 10. 2026: Moje výplata sloučená do Reportu (karta Moje odměna ukáže i schválený měsíc z HR).
  { to: "/hr/time-report", label: "Report", icon: Clock, roles: ["sales"] },
  // 15. 10. 2026: Moje školení (přidělená videa, prezentace a testy). Trenér ho má v Trenéři, BC trenér v Bootcamps.
  { to: "/moje-skoleni", label: "Moje školení", icon: GraduationCap, roles: ["sales","manager","csm","nutritionist","marketing"] },
  // === Manažer Time reporting — přehled denních time reportů týmu (+ vlastní měření) ===

  // === CEO sekce — sdružuje vlastnické přehledy (Přehled + Reporting + Cashflow) ===
  {
    label: "CEO",
    icon: Briefcase,
    roles: ["owner","manager"],
    children: [
      { to: "/dashboard", label: "Přehled", icon: LayoutDashboard, roles: ["owner","manager"] },
      { to: "/weekly", label: "Weekly report", icon: CalendarRange, roles: ["owner","manager"] },
      { to: "/monthly", label: "Monthly report", icon: CalendarCheck, roles: ["owner","manager"] },
      { to: "/yearly", label: "Yearly report", icon: CalendarClock, roles: ["owner","manager"] },
      { to: "/stats", label: "Total statistics", icon: BarChart3, roles: ["owner","manager"] },
      // 1. 10. 2026: Sales report přesunut ze sekce Sales sem, ať ho má owner v přehledech (Láďa).
      { to: "/sales/report", label: "Sales report", icon: HeartHandshake, roles: ["owner","manager"] },
      { to: "/reporting/treneri", label: "Reporting coaches", icon: Users, roles: ["owner","manager"] },
      { to: "/cashflow", label: "Cashflow", icon: Wallet, roles: ["owner"] },
      { to: "/marketing", label: "Marketing report", icon: TrendingUp, roles: ["owner","manager"] },
      // 22.9.2026: App reporting je jen v sekci Analytika (Klientská aplikace).
      // Manažer analytiku nevidí vůbec (rozhodnutí Láďi po auditu).
    ],
  },
  // === Operations — task management, meetingy, time tracking a CEO přehled ===
  // Přesun z Notionu do appky (7/2026). Zatím jen owner, další role odkryjeme postupně.
  {
    label: "Operations",
    icon: ClipboardList,
    roles: ["owner"],
    children: [
      // Přehled skrytý (20.7.2026) — owner si ho přidá jako sekci do svého CEO přehledu.
      // { to: "/operations/prehled", label: "Přehled", icon: LayoutDashboard, roles: ["owner"] },
      { to: "/operations/ukoly", label: "Úkoly", icon: ListChecks, roles: ["owner"] },
      { to: "/manager/treneri/ukoly", label: "Úkoly trenérů", icon: Users, roles: ["owner"] },
      // 6. 10. 2026 (Láďa): vzorový rozvrh s kartami trenérů i pro ownera (manažer a CSM ho mají v Trenéřích).
      { to: "/manager/treneri/vzor", label: "Vzorový rozvrh", icon: CalendarRange, roles: ["owner"] },
      { to: "/operations/meetingy", label: "Meetingy", icon: CalendarCheck, roles: ["owner"] },
      // Time tracking přesunutý z HR (owner tam měl jen přehled, teď je v Operations).
      { to: "/operations/time-tracking", label: "Time tracking", icon: Clock, roles: ["owner"] },
      // 7.8.2026: kdo a kdy posunul deadline / přehodil pilota (oba boardy). Jen owner.
      { to: "/operations/historie-ukolu", label: "Historie úkolů", icon: History, roles: ["owner"] },
    ],
  },
  // === Operations (manager + CSM) ===
  // 15.7.2026: CSM má Time tracking v sekci Operations místo HR (dle zadání ownera).
  // 17.7.2026: + Úkoly (management úroveň kanbanu) pro managera i CSM.
  {
    label: "Operations",
    icon: ClipboardList,
    roles: ["manager", "csm"],
    children: [
      { to: "/operations/ukoly", label: "Úkoly", icon: ListChecks, roles: ["manager", "csm"] },
      { to: "/operations/meetingy", label: "Meetingy", icon: CalendarCheck, roles: ["manager", "csm"] },
      { to: "/hr/time-report", label: "Time tracking", icon: Clock, roles: ["csm", "manager"] },
    ],
  },
  // === HR — centrální evidence týmu (Lidé, Bonusy, Zálohy a srážky, Dovolené, Dokumenty) ===
  // Přesunuto nad Manažer (22.5.2026 dle požadavku uživatele). Dokumenty přesunuté
  // z hlavního menu sem, ať jsou všechny věci k týmu na jednom místě.
  // Owner vidí vše, manager vidí Lidé+Dovolené, u ne-trenérů jen jméno+kontakty.
  {
    label: "HR",
    icon: Briefcase,
    roles: ["owner","manager","csm","nutricni"],
    children: [
      // Time report — vyplňuje back-office (sales/marketing/nutricni).
      // 1.7.2026: skryto pro manager + csm (pro ně teď k ničemu).
      // 10.7.2026: owner přesunut do Operations > Time tracking (stejná stránka), tady už ho nemá.
      // 15.7.2026: CSM přesunut do vlastní skupiny Operations (níže). Manager má vlastní top položku.
      { to: "/hr/time-report", label: "Time report", icon: Clock, roles: ["nutricni"] },
      { to: "/hr/lide", label: "Lidé", icon: Users, roles: ["owner","manager"] },
      // 10.8.2026: interni skoleni + testy pro tym (odkaz s tokenem, limit 10 min).
      { to: "/hr/skoleni", label: "Školení", icon: GraduationCap, roles: ["owner","manager"] },
      // 20. 10. 2026 (Láďa): CSM a manažer neviděli svá přidělená školení (Moje školení je
      // u nich mimo povolené skupiny), proto i tady v HR.
      { to: "/moje-skoleni", label: "Moje školení", icon: GraduationCap, roles: ["manager","csm"] },
      // 15. 10. 2026: Kniha úrazů (záznamy o úrazech, podpis přes SMS).
      { to: "/kniha-urazu", label: "Kniha úrazů", icon: Bandage, roles: ["owner","manager","csm"] },
      // 19.8.2026: jednotny podpis do Gmailu, vyplni se a zkopiruje. Nic se neuklada.
      { to: "/hr/podpis", label: "Podpis do Gmailu", icon: Mail, roles: ["owner","manager","csm"] },
      // 6.8.2026: potenciální trenéři v užším výběrovém řízení + jejich vstupní test.
      // 14.9.2026 (Láďa): přesunuto ze sekce Trenéři sem do HR, manažer to má
      // vidět i tady a pracovat s tím z jednoho místa.
      { to: "/hr/kandidati", label: "Potenciální trenéři", icon: UserPlus, roles: ["owner", "manager"] },
      { to: "/hr/vyplaty", label: "Výplaty / fakturace", icon: Receipt, roles: ["owner"] },
      // 24.8.2026: rocni prehledy, drive vedene v Google Sheetu.
      { to: "/hr/dpp", label: "Přehled DPP hodin", icon: Clock, roles: ["owner"] },
      { to: "/hr/naklady", label: "Náklady na lidi", icon: Coins, roles: ["owner"] },
      { to: "/hr/bonusy-srazky", label: "Bonusy a srážky", icon: Award, roles: ["owner","manager","csm"], badgeKey: "bs_caka" },
      { to: "/hr/upominky", label: "Upomínky", icon: AlertTriangle, roles: ["owner","manager"], badgeKey: "upominky_at_limit" },
      { to: "/hr/absence", label: "Absence", icon: Plane, roles: ["owner","manager","csm"], badgeKey: "absence_pending" },
      { to: "/dokumenty", label: "Dokumenty", icon: BookOpen, roles: ["owner","manager"] },
    ],
  },
  // === Manažer portál — operativní pohled mezi CEO a trenérem ===
  {
    // Manažerská sekce Trenéři jako dropdown (jako Clients management). Roles jen manager,
    // ať se nemíchá s trenérskou sekcí "Trenéři" (owner/trener) níž.
    label: "Trenéři",
    icon: Users,
    roles: ["manager", "csm"],
    children: [
      { to: "/manager/treneri/ukoly", label: "Úkoly", icon: ListChecks, roles: ["manager", "csm"] },
      { to: "/manager/treneri/kalendar", label: "Kalendář", icon: CalendarPlus, roles: ["manager", "csm"] },
      { to: "/manager/treneri/rozvrh", label: "Rozvrh", icon: Calendar, roles: ["manager", "csm"] },
      { to: "/manager/treneri/vzor", label: "Vzorový rozvrh", icon: CalendarRange, roles: ["manager", "csm"] },
      { to: "/manager/treneri/reporting", label: "Reporting coaches", icon: BarChart3, roles: ["manager", "csm"] },
      // 14.9.2026 (Láďa): Potenciální trenéři přesunuti odsud do HR.
      { to: "/moje-vyplata", label: "Moje výplata", icon: Receipt, roles: ["manager"] },
    ],
  },
  // === Trenérský portál — dropdown s podstránkami trenéra ===
  // Owner vidí všechny s impersonací, trenér jen své.
  {
    label: "Trenéři",
    icon: Users,
    roles: ["owner","trener"],
    children: [
      { to: "/trener/dashboard", label: "Přehled", icon: LayoutDashboard, roles: ["owner","trener"] },
      { to: "/trener/kalendar", label: "Kalendář", icon: CalendarPlus, roles: ["owner","trener"] },
      { to: "/trener/rozvrh-vzor", label: "Rozvrh", icon: Calendar, roles: ["owner","trener"] },
      { to: "/trener/klienti", label: "Klienti", icon: UserCircle, roles: ["owner","trener"] },
      { to: "/trener/ukoly", label: "Úkoly", icon: ListChecks, roles: ["owner","trener"] },
      { to: "/trener/denik", label: "Deník úspěchů", icon: Trophy, roles: ["trener"] },
      { to: "/trener/kniha-urazu", label: "Kniha úrazů", icon: Bandage, roles: ["trener"] },
      // 16. 10. 2026: jedna položka Školení (dlaždice Školení, Testy, Dokumenty) místo Dokumentů a Moje školení.
      { to: "/trener/skoleni", label: "Školení", icon: GraduationCap, roles: ["owner","trener"] },
      // Trenéři jsou OSVČ partneři → "Fakturace", ne "Výplata". Úplně dole.
      // 30. 10. 2026 (#186): manažer v roli Trenér ji nemá, výplatu vidí v Moje výplata.
      { to: "/trener/fakturace", label: "Fakturace", icon: Receipt, roles: ["owner","trener","bc_trener"], hideRealRoles: ["manager"] },
    ],
  },
  // === Sales portál — owner a manažer (sales má od 1. 10. 2026 ploché menu nahoře) ===
  {
    label: "Sales",
    icon: HeartHandshake,
    roles: ["owner","manager"],
    children: [
      // 30. 9. 2026: nová sales sekce (schválený mockup v8). Dnes, Kontakty a Zkušební jsou
      // záložky uvnitř. Sales report je od 1. 10. 2026 v sekci CEO (Láďa).
      { to: "/sales/dnes", label: "Volání", icon: HeartHandshake, roles: ["owner","manager"] },
      { to: "/hodnoceni-vyzivy", label: "Hodnocení výživy", icon: Soup, roles: ["owner"], badgeKey: "hodnoceni_vyzivy" },
    ],
  },
  // === B2B (20. 10. 2026, fáze 2): CRM firem pro Dny zdraví, přednášky a programy. Owner a manažer. ===
  {
    label: "B2B",
    icon: Building2,
    roles: ["owner","manager"],
    children: [
      { to: "/b2b/prehled", label: "Přehled", icon: LayoutDashboard, roles: ["owner","manager"] },
      { to: "/b2b/firmy", label: "Firmy", icon: Building2, roles: ["owner","manager"] },
      { to: "/b2b/akce", label: "Akce", icon: CalendarCheck, roles: ["owner","manager"] },
      { to: "/b2b/cenik", label: "Nabídka a ceník", icon: Tag, roles: ["owner","manager"] },
    ],
  },
  // === Nutritionist (1.10.2026, schválený mockup) ===
  // Dřív to bylo dvanáct plochých položek pod sebou a výživová se v tom ztrácela.
  // Teď nahoře to, co otevírá denně (Dashboard, Úkoly), a pod tím čtyři skupiny
  // podle toho, čeho se práce týká: konkrétní klientky, jídelníčky, klub, ona sama.
  // Skupiny jsou stejný prvek jako u ostatních rolí, nic nového se nezavádí.
  { to: "/nutri/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["nutritionist"] },
  // Úkoly (17.7.2026) — basic úroveň company kanbanu pro sales a nutritionist (plochá položka).
  { to: "/operations/ukoly", label: "Úkoly", icon: ListChecks, roles: ["sales", "nutritionist"] },
  {
    label: "Klientky",
    icon: UserCircle,
    roles: ["nutritionist"],
    children: [
      { to: "/clients", label: "Klienti", icon: UserCircle, roles: ["nutritionist"] },
      // Konzultace (29.9.2026): volno na online konzultace po týdnech, úkol na příští měsíc.
      { to: "/nutri/konzultace", label: "Konzultace", icon: CalendarCheck, roles: ["nutritionist"] },
      // Deník úspěchů (3.8.2026) — endpoint denik-admin i route /denik ji pouští, takže
      // může i psát a komentovat.
      { to: "/denik", label: "Deník úspěchů", icon: Trophy, roles: ["nutritionist"] },
    ],
  },
  {
    label: "Jídelníčky",
    icon: ChefHat,
    roles: ["nutritionist"],
    children: [
      // PDF jídelníček na měsíc (14.7.2026) — generování obsáhlého dokumentu k tisku.
      { to: "/nutri/pdf-jidelnicky", label: "Jídelníček PDF", icon: FileText, roles: ["nutritionist"] },
      // Databáze jídelníček (22.8.2026, fáze 2) — recepty a texty generátoru ke schválení.
      { to: "/vyziva/databaze", label: "Databáze jídelníček", icon: Database, roles: ["nutritionist"] },
      // Návrhy receptů (20.8.2026) — recepty navržené klientkami: stav, kdo navrhl, zpětná úprava.
      { to: "/nutri/navrhy-receptu", label: "Návrhy receptů", icon: ChefHat, roles: ["nutritionist"] },
    ],
  },
  {
    label: "Klub",
    icon: Layers,
    roles: ["nutritionist"],
    children: [
      // Jídelníček klub (27.8.2026) a Návyky klub: obsah pro členky klubu, ne pro
      // jednotlivé klientky. Owner má vlastní, širší skupinu Klub níž.
      { to: "/klub/jidelnicek", label: "Jídelníček klub", icon: ChefHat, roles: ["nutritionist"] },
      { to: "/klub/navyky", label: "Návyky klub", icon: ListChecks, roles: ["nutritionist"] },
      // Dotazy k živému vysílání (7.10.2026): Marťa je ve vysílání zodpovídá.
      { to: "/klub/dotazy", label: "Dotazy k vysílání", icon: MessageCircleQuestion, roles: ["nutritionist"] },
    ],
  },
  {
    label: "Moje",
    icon: UserCog,
    roles: ["nutritionist"],
    children: [
      // Time report (14.7.2026) a Moje výplata (29.7.2026) — její vlastní agenda.
      { to: "/hr/time-report", label: "Time report", icon: Clock, roles: ["nutritionist"] },
      { to: "/moje-vyplata", label: "Moje výplata", icon: Receipt, roles: ["nutritionist"], hideRealRoles: ["csm"] },
    ],
  },
  // === Clients management — sjednocuje Klienti a Schvalování propadlých tréninků ===
  // Sales nevidí (sales workflow je v Sales sekci výš). Trenér má vlastní /trener/klienti.
  {
    label: "Clients management",
    icon: UserCircle,
    roles: ["owner","manager","csm"],
    children: [
      // Manažer: read-only přehled VŠECH klientů (+ komentáře). Vlastní route /manager/clients.
      { to: "/manager/clients", label: "Klienti", icon: UserCircle, roles: ["manager"] },
      { to: "/clients", label: "Klienti", icon: UserCircle, roles: ["owner","csm"] },
      // Rezervace (29.9.2026): hned pod Klienty, owner, manager i CSM (sales má plochou položku).
      { to: "/rezervace", label: "Rezervace", icon: CalendarPlus, roles: ["owner","manager","csm"] },
      // 1. 11. 2026: Hodnocení výživy (obvolávání po nutriční konzultaci), CSM. Owner ho má v sekci Sales.
      { to: "/hodnoceni-vyzivy", label: "Hodnocení výživy", icon: Soup, roles: ["csm"], badgeKey: "hodnoceni_vyzivy" },
      // Deník úspěchů (16.7.2026) — feed z klientské aplikace, owner ho chce po ruce u klientů.
      // 17.7.2026: + manager a CSM (dle zadání ownera), hned pod Klienty.
      { to: "/denik", label: "Deník úspěchů", icon: Trophy, roles: ["owner", "manager", "csm", "nutritionist"] },
      // 17. 10. 2026: přehled fakturace, vidí jen owner a CSM.
      { to: "/clients/fakturace", label: "Fakturace", icon: Receipt, roles: ["owner","csm"] },
      { to: "/clients/permanentky", label: "Permanentky", icon: Award, roles: ["owner","csm"] },
      { to: "/clients/log-expired", label: "Propadlé tréninky", icon: AlertTriangle, roles: ["owner","csm"] },
      { to: "/clients/log-st-bonus", label: "Log ST/Bonus", icon: History, roles: ["owner"] },
      { to: "/clients/log-pripsane-treninky", label: "Log připsaných tréninků", icon: ListChecks, roles: ["owner"] },
      { to: "/clients/volno", label: "Dovolené klientů", icon: Umbrella, roles: ["owner","manager","csm"] },
      // 30.6.2026 feedback #23/#27: CSM potřebuje Log Cash (zápis hotovostních plateb).
      // 31.10.2026 (zadání Kristiny): manažer (Michal) zapisuje hotovost od 1. 1. 2027.
      { to: "/log-cash", label: "Log Cash", icon: Banknote, roles: ["owner","csm","manager"], odDataProRoli: { manager: "2027-01-01" } },
    ],
  },
  // === Bootcamps (16.9.2026) — vlastní sekce, ne položka v Clients management ===
  // Účastnice bootcampu nejsou v clients, mají vlastní tabulky, vlastní platby
  // i vlastní lokality. Manažer a CSM to potřebují vidět jako celek, ne jako
  // jeden řádek schovaný mezi klienty.
  //
  // Trenér koučinku skupinu vidí, jen když mu je nějaká lokalita přiřazená
  // (zjišťuje se přes /api/bootcampy?mujBeh=1), a vidí v ní jen Lokality.
  // Klientky jsou provozní agenda, do té trenérovi koučinku nic není.
  //
  // 16.9.2026 (zadání Ládi): role bc_trener je člověk najatý čistě na bootcampy.
  // Ten vidí Lokality, Klientky a Fakturaci, a nic jiného. Bootcamps mu v menu
  // svítí vždycky, i než mu lokalitu přiřadíme, jinak by po přihlášení viděl
  // prázdno a myslel si, že mu appka nejde.
  {
    label: "Bootcamps",
    icon: Flame,
    roles: ["owner","manager","csm","head_coach","trener","bc_trener"],
    jenSBootcampem: true,
    children: [
      { to: "/bootcampy", label: "Lokality", icon: MapPin, roles: ["owner","manager","csm","head_coach","trener","bc_trener"] },
      // 16.9.2026: BC trenér Klientky vidí. Potřebuje u ženy na své lekci
      // najít komentář (zranění, domluvená výjimka), server mu stejně pošle
      // jen lidi z jeho lokality. Klasický trenér koučinku sem dál nevidí.
      { to: "/bootcampy/klienti", label: "Klientky", icon: Users, roles: ["owner","manager","csm","head_coach","bc_trener"] },
      { to: "/kniha-urazu", label: "Kniha úrazů", icon: Bandage, roles: ["head_coach","bc_trener"] },
      { to: "/moje-skoleni", label: "Moje školení", icon: GraduationCap, roles: ["head_coach","bc_trener"] },
    ],
  },
  // === Reporting (manažer) — Přehled / Monthly / Yearly jako CEO, cíle read-only ===
  {
    label: "Reporting",
    icon: BarChart3,
    roles: ["manager", "csm"],
    children: [
      { to: "/manager/reporting/monthly", label: "Monthly report", icon: CalendarCheck, roles: ["manager", "csm"] },
      { to: "/manager/reporting/yearly", label: "Yearly report", icon: CalendarClock, roles: ["manager", "csm"], locked: true },
    ],
  },
  // === Výživa (owner) — databáze receptů se schvalováním + generování PDF ===
  // (22.8.2026, fáze 2 přestavby generátoru jídelníčků.)
  {
    label: "Výživa",
    icon: ChefHat,
    roles: ["owner"],
    children: [
      { to: "/vyziva/databaze", label: "Databáze jídelníčku", icon: Database, roles: ["owner"] },
      { to: "/nutri/pdf-jidelnicky", label: "Jídelníček PDF", icon: FileText, roles: ["owner"] },
      { to: "/nutri/navrhy-receptu", label: "Návrhy receptů", icon: ChefHat, roles: ["owner"] },
    ],
  },
  // === Klub (26.8.2026) — klub.fitway40.cz ===
  // Sdílené kalorické úrovně jídelníčku a katalog návyků. Nutriční
  // specialistka obojí vidí taky, jen ne jako skupinu (má ploché menu).
  {
    label: "Klub",
    icon: HeartHandshake,
    // 25.10.2026: csm (Tinka) vidí v Klubu jen Aktivitu členek, ostatní
    // položky mají dál roles: ["owner"] (Láďa).
    roles: ["owner", "csm"],
    children: [
      { to: "/klub/jidelnicek", label: "Jídelníček klub", icon: ChefHat, roles: ["owner"] },
      { to: "/klub/navyky", label: "Návyky", icon: ListChecks, roles: ["owner"] },
      // Členové klubu (31.8.2026) — seznam členek a ruční přidání; v Klientech nejsou.
      { to: "/klub/clenove", label: "Členové", icon: Users, roles: ["owner"] },
      // Aktivita členek (25.10.2026): kdo klub opravdu používá a kdo se ztrácí.
      { to: "/klub/aktivita", label: "Aktivita členek", icon: Activity, roles: ["owner", "csm"] },
      // Dotazy k živému vysílání (7.10.2026), členky je posílají předem z klubu.
      { to: "/klub/dotazy", label: "Dotazy k vysílání", icon: MessageCircleQuestion, roles: ["owner"] },
      // Historie členství (8.10.2026): zrušení, měsíc zdarma, přechody, a co na to FAPI.
      { to: "/klub/clenstvi", label: "Členství", icon: UserRoundCog, roles: ["owner"] },
    ],
  },
  // === Systém — provozní přehledy a konfigurace (automatizace, audit, pravidla) ===
  // Sjednocuje 3 dříve samostatné položky do jednoho dropdownu pro přehlednější menu.
  {
    label: "Systém",
    icon: Server,
    roles: ["owner","manager"],
    children: [
      { to: "/automatizace", label: "Automatizace", icon: Zap, roles: ["owner","manager"] },
      { to: "/system/changelog", label: "Change log", icon: History, roles: ["owner","manager"] },
      { to: "/audit-log", label: "Audit log", icon: History, roles: ["owner","manager"] },
      { to: "/naklady-ai", label: "Náklady AI", icon: Cpu, roles: ["owner"] },
      { to: "/feedback", label: "Hlášení uživatelů", icon: MessageSquare, roles: ["owner","manager"] },
      { to: "/pravidla", label: "Pravidla a výjimky", icon: ShieldCheck, roles: ["owner"] },
      { to: "/sms-sablony", label: "SMS šablony", icon: MessageSquare, roles: ["owner","manager"] },
      { to: "/clients-app/popupy", label: "Pop-upy v appce", icon: LayoutGrid, roles: ["owner"] },
    ],
  },
  // === Analytika — web, produkty, reklamy, klientská aplikace a pondělní report ===
  // 22.9.2026 (schválený mockup „Analytika v adminu“): jedna sekce místo
  // Analytiky webu ve Webu a App reportingu v Reportingu. Stránky Web a
  // Klientská aplikace jsou původní obrazovky beze změny obsahu.
  {
    label: "Analytika",
    icon: LineChart,
    // 17.10.2026: csm (Tinka) vidí jen Web podrobně kvůli prodeji klubu (Láďa).
    roles: ["owner", "csm"],
    children: [
      { to: "/analytika/prehled", label: "Přehled", icon: LineChart, roles: ["owner"] },
      { to: "/analytika/produkty", label: "Produkty a stránky", icon: Target, roles: ["owner"] },
      { to: "/analytika/reklamy", label: "Reklamy", icon: Megaphone, roles: ["owner"] },
      { to: "/web/analytika", label: "Web podrobně", icon: Globe, roles: ["owner", "csm"] },
      { to: "/app-reporting", label: "Klientská aplikace", icon: Smartphone, roles: ["owner"] },
      { to: "/analytika/reporty", label: "Pondělní reporty", icon: MessageSquare, roles: ["owner"] },
    ],
  },
  // === Web — přehled a správa veřejného webu fitway40.cz (SEO, marketingové kódy) ===
  // Nový modul (7/2026), zatím jen owner. První položka: Mapa webu.
  {
    label: "Web",
    icon: Globe,
    roles: ["owner","manager"],
    children: [
      { to: "/web/mapa", label: "Mapa webu", icon: MapIcon, roles: ["owner"] },
      { to: "/web/zmeny", label: "Log změn webu", icon: History, roles: ["owner","manager"] },
      { to: "/web/blog", label: "Blog", icon: MessageSquare, roles: ["owner","manager"] },
    ],
  },
  // Legal (19.10.2026, varianta A): záznam o činnostech zpracování a šablony smluv.
  { to: "/legal", label: "Legal", icon: Scale, roles: ["owner"] },
  { to: "/settings", label: "Nastavení", icon: SettingsIcon, roles: ["owner"] },
];

/* ==========================================================================
   Filtrování podle role. Doslovný přepis původní logiky ze Sidebaru.
   ========================================================================== */

/** Pořadí skupin v menu pro manažera a CSM. */
const PORADI = {
  manager: ["Dashboard", "Reporting", "Trenéři", "Clients management", "Bootcamps", "Operations", "HR"],
  csm: ["Dashboard", "Clients management", "Bootcamps", "Trenéři", "Reporting", "Operations", "HR", "Analytika", "Klub"],
};

/** Které skupiny manažer a CSM vůbec vidí. */
const POVOLENE_SKUPINY = {
  manager: ["Dashboard", "Trenéři", "Clients management", "Bootcamps", "Reporting", "Operations", "HR"],
  csm: ["Dashboard", "Clients management", "Bootcamps", "Trenéři", "Reporting", "Operations", "HR", "Analytika", "Klub"],
};

/**
 * Položky menu pro danou roli, včetně skupin a jejich podpoložek.
 * Vrací stejnou strukturu jako `navItems`, jen profiltrovanou.
 */
/**
 * Kalendář rezervací je modul chystaného rezervačního systému. 12.9.2026 ho
 * má vidět POUZE owner a testovací trenér; u trenérů to hlídá per osoba
 * `rezervace_kalendar` z /api/my-trainer, u manažera a CSM nic nehlídalo nic,
 * takže ho viděli, i když spuštěný není.
 *
 * Až se systém spustí, přepni tuhle konstantu na true a kalendář se objeví
 * manažerovi i CSM. Trenéři ho dostávají dál po jednom přes svůj příznak.
 */
export const KALENDAR_REZERVACI_SPUSTEN = true; // 29.9.2026: přechod z Reservia, trenéři od 1. 10.

export function polozkyProRoli(role, ctx = {}) {
  const { realRole = "", trenerUkoncen = false, kalendarZapnuty = true, maBootcamp = false } = ctx;

  const zaklad = trenerUkoncen
    // Ukončený trenér: celé menu zmizí, zůstává jediná položka Fakturace.
    ? [{ to: "/trener/fakturace", label: "Fakturace", icon: Receipt }]
    // Trenér (i náhled ownera): dropdown "Trenéři" je pro něj zbytečný —
    // podstránky se ukážou přímo v menu jako plochý seznam (17.7.2026).
    : (role === "trener" || role === "bc_trener")
      ? navItems.flatMap((it) => (it.label === "Trenéři" && it.children ? it.children : [it]))
      : navItems;

  return zaklad
    // Filtruj per role — pokud role není v item.roles, položka se nezobrazí.
    .filter((item) => !item.roles || !role || item.roles.includes(role))
    .filter((item) => kalendarZapnuty || item.to !== "/trener/kalendar")
    // Bootcampy: trenér bez přiřazeného běhu tam nemá co dělat.
    .filter((item) => !item.jenSBootcampem || role !== "trener" || maBootcamp)
    // Skryj položku v náhledu, když reálná role je v hideRealRoles.
    .filter((item) => !item.hideRealRoles || !(realRole && item.hideRealRoles.includes(realRole)))
    .filter((item) => role !== "manager" || POVOLENE_SKUPINY.manager.includes(item.label))
    .filter((item) => role !== "csm" || POVOLENE_SKUPINY.csm.includes(item.label))
    .sort((a, b) => {
      const ord = PORADI[role];
      if (!ord) return 0;
      return ord.indexOf(a.label) - ord.indexOf(b.label);
    })
    .map((item) => {
      if (!item.children) return item;
      const viditelne = item.children.filter((c) => {
        if (c.roles && role && !c.roles.includes(role)) return false;
        // Stejné pravidlo jako u položek nejvyšší úrovně: v náhledu za jinou roli
        // se položka schová, když reálná role je v hideRealRoles (1.10.2026, kvůli
        // Moje výplata ve skupině Moje u výživové).
        if (c.hideRealRoles && realRole && c.hideRealRoles.includes(realRole)) return false;
        // Položka pro roli až od data (YYYY-MM-DD, pražský čas není potřeba, stačí den).
        if (c.odDataProRoli && c.odDataProRoli[role]) {
          const d = new Date();
          const dnes = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          if (dnes < c.odDataProRoli[role]) return false;
        }
        // Kalendář rezervací je zatím jen pro ownera a testovacího trenéra.
        if (!KALENDAR_REZERVACI_SPUSTEN && c.to === "/manager/treneri/kalendar") return false;
        // HR je pro manažera a CSM otevřené jen kvůli podpisu do Gmailu
        // (19.8.2026). Zbytek HR mají jinde nebo tam pro ně není co dělat.
        // Manager má v HR i Školení (21.8.2026), CSM podpis a Bonusy a srážky
        // (31.8.2026 — zadává bonusy trenérům, schválit ani vyplatit nemůže).
        // 14.9.2026: manager má v HR i Potenciální trenéry (přesun ze sekce Trenéři).
        // 21.9.2026: manager i CSM mají v HR Absence (zadávají volno, schvaluje owner).
        if (item.label === "HR" && role === "manager" && !["/hr/podpis", "/hr/skoleni", "/moje-skoleni", "/hr/kandidati", "/hr/absence", "/kniha-urazu"].includes(c.to)) return false;
        if (item.label === "HR" && role === "csm" && !["/hr/podpis", "/hr/bonusy-srazky", "/hr/absence", "/kniha-urazu", "/moje-skoleni"].includes(c.to)) return false;
        return true;
      });
      if (viditelne.length === 0) return null;
      return { ...item, children: viditelne };
    })
    .filter(Boolean);
}

/**
 * Plochý seznam cest, které role v menu vidí. Používá to kontrolní skript,
 * aby po přestavbě sedělo menu na položku přesně jako před ní.
 */
export function cestyProRoli(role, ctx = {}) {
  const out = [];
  for (const item of polozkyProRoli(role, ctx)) {
    if (item.children) for (const c of item.children) out.push(c.to);
    else if (item.to) out.push(item.to);
  }
  return out;
}

/* ==========================================================================
   Spodní lišta na telefonu
   ========================================================================== */

/**
 * Čtyři hlavní položky spodní lišty pro roli. Pátá (Více) otevírá sheet se
 * všemi ostatními sekcemi, takže se tady nic neztratí, jen se to schová
 * o klepnutí dál.
 *
 * Rozhodnutí z fáze 0 (zadání verze 1.1): nasazuje se tahle sada a dolaďuje
 * se podle provozu. Změna je jeden řádek tady.
 *
 * Cesty se vždycky prosejí přes to, co role opravdu vidí, a když jich po
 * prosetí zbyde míň než tři, doplní se ze začátku jejího menu. Nikdy se
 * tedy nemůže stát, že by v liště svítila položka, na kterou role nemá právo.
 */
const MOBILNI_SADY = {
  owner: ["/dashboard", "/operations/ukoly", "/clients", "/cashflow"],
  // 12.9.2026: na místě kalendáře rezervací jsou Úkoly trenérů, dokud se
  // rezervační systém nespustí (viz KALENDAR_REZERVACI_SPUSTEN).
  // 29.9.2026: Rezervace (seznam a kalendář týmu) je denní práce manažera, CSM i sales.
  manager: ["/manager/dashboard", "/rezervace", "/operations/ukoly", "/manager/clients"],
  csm: ["/csm/dashboard", "/rezervace", "/clients", "/operations/ukoly"],
  // 1. 10. 2026: sales má jen čtyři položky, všechny jsou rovnou v liště.
  // 20. 10. 2026: Rozvrhy trenérů hned za Voláním.
  // 1. 11. 2026: Hodnocení výživy hned za Voláním (odznak s počtem i na mobilu), Report pod Více.
  sales: ["/sales/dnes", "/hodnoceni-vyzivy", "/rozvrhy-treneru", "/operations/ukoly"],
  marketing: ["/dashboard", "/marketing", "/weekly", "/hr/time-report"],
  nutritionist: ["/nutri/dashboard", "/nutri/konzultace", "/clients", "/operations/ukoly"],
  nutricni: ["/hr/time-report"],
  trener: ["/trener/dashboard", "/trener/klienti", "/trener/ukoly", "/trener/kalendar"],
};

/**
 * Trenérská sada do spodní lišty. Tři položky, čtvrtá dlaždice jsou tři tečky.
 *
 * Třetí položka se přepíná podle toho, jestli má trenér zapnutý kalendář
 * rezervací. Dokud ho nemá, potřebuje denně Úkoly; jakmile ho dostane, je
 * kalendář to hlavní, co v portálu otevírá, a Úkoly se stěhují pod Více.
 */
function sadaTrenera(kalendarZapnuty) {
  // 4.9.2026: čtyři sekce a pátá dlaždice jsou tři tečky. Dokud trenér nemá
  // zpřístupněný kalendář rezervací, je na jeho místě Rozvrh.
  return kalendarZapnuty
    ? ["/trener/dashboard", "/trener/klienti", "/trener/ukoly", "/trener/kalendar"]
    : ["/trener/dashboard", "/trener/klienti", "/trener/ukoly", "/trener/rozvrh-vzor"];
}

/**
 * Vrací nejvýš čtyři položky do spodní lišty, pátá dlaždice jsou tři tečky.
 *
 * Když je člověk kdekoli v trenérském portálu, dostane trenérskou sadu bez
 * ohledu na roli. Platí to i pro ownera, který se dívá za trenéra: v portálu
 * potřebuje trenérské ovládání, ne svoje CEO reporty.
 */
export function mobilniPolozky(role, pathname = "", ctx = {}) {
  const { kalendarZapnuty = true, trenerUkoncen = false } = ctx;
  const dostupne = polozkyProRoli(role, ctx);

  // Plochý index cesta → položka, ať se dá k cestě dohledat popisek a ikona.
  const index = new Map();
  for (const item of dostupne) {
    if (item.children) for (const c of item.children) index.set(c.to, c);
    else if (item.to) index.set(item.to, item);
  }

  const vTrenerskemPortalu = String(pathname).startsWith("/trener/");
  const pozadovane = trenerUkoncen
    ? ["/trener/fakturace"]
    : vTrenerskemPortalu
      ? sadaTrenera(kalendarZapnuty)
      : MOBILNI_SADY[role] || [];

  const vybrane = [];
  for (const cesta of pozadovane) {
    const p = index.get(cesta);
    if (p) vybrane.push(p);
  }
  // Doplnění, kdyby role některou z požadovaných cest neměla.
  for (const [, p] of index) {
    if (vybrane.length >= 4) break;
    if (!vybrane.includes(p)) vybrane.push(p);
  }
  return vybrane.slice(0, 4);
}

/* ==========================================================================
   Název stránky do horní lišty
   ========================================================================== */

/** Cesta → popisek, poskládané z menu, takže žádná sekce nezůstane bez názvu. */
const NAZVY = (() => {
  const m = new Map();
  for (const item of navItems) {
    if (item.children) for (const c of item.children) m.set(c.to, c.label);
    else if (item.to) m.set(item.to, item.label);
  }
  // Stránky, které v menu nejsou (detaily a podstránky).
  m.set("/b2b/firma", "Firmy");
  m.set("/trainers", "Trenéři");
  m.set("/moje-vyplata", "Moje výplata");
  m.set("/denik", "Deník úspěchů");
  return m;
})();

/**
 * Název stránky pro horní lištu. Bere nejdelší shodnou cestu, takže detail
 * klientky pod /clients/… dostane název sekce místo obecného "FW40 admin".
 */
export function titleFor(pathname, role = "") {
  const cesta = String(pathname || "");
  // Sales má Time report přejmenovaný na Report (hodiny a výplata na jednom místě).
  if (role === "sales" && cesta === "/hr/time-report") return "Report";
  if (NAZVY.has(cesta)) return NAZVY.get(cesta);
  let nejlepsi = "";
  for (const klic of NAZVY.keys()) {
    if (klic !== "/" && cesta.startsWith(klic + "/") && klic.length > nejlepsi.length) nejlepsi = klic;
  }
  return nejlepsi ? NAZVY.get(nejlepsi) : "FW40 admin";
}
